/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Texture, Circle } from 'pixi.js-legacy';
import { SpriteTicket } from '@appPlanApplication/tickets/SpriteTicket';
import { TicketContainer } from '@appPlanApplication/tickets/TicketContainer';
import { Side } from '@touchplan/common';
import { ticketSchema } from '../ticket-components/TicketSchema';
import { IMAGE_PADDING } from '@appCanvasCommon/interfaces/image-padding.interface';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { TicketType } from '@touchplan/common';
import { setTicketWidthHelper } from 'src/app/utils/ticket-utils';
/**
 * This class is the main representation of a constraint. It has a simple component api to add individual pieces associated with the same data
 */
export class SpriteConstraint extends SpriteTicket {
	public override type: string = TicketType.Constraint;
	public isPull = false;
	private canvasAngularLand: CanvasAngularLand;
	/**
	 * Represents the texture that is drawn on the active side.
	 */
	private _activeConstraintTexture: Texture;
	/**
	 * Represents the texture that is drawn on the pull side.
	 */
	private _pullConstraintTexture: Texture;
	/**
	 * @param {PIXI.Texture} ticketShape
	 * @param {PIXI.Texture} ticketShapePull
	 * @param {CanvasAngularLand} canvasAngularLand
	 * @param {TicketContainer} ticketContainer
	 */
	constructor(
		ticketShape: Texture,
		ticketShapePull: Texture,
		canvasAngularLand: CanvasAngularLand,
		ticketContainer: TicketContainer
	) {
		super(ticketShape, canvasAngularLand, ticketContainer);
		this._activeConstraintTexture = ticketShape;
		this._pullConstraintTexture = ticketShapePull;
		this.canvasAngularLand = canvasAngularLand;

		const data = ticketContainer.getData();

		this.x = -IMAGE_PADDING.ACTIVE_SHAPE;
		this.y = -IMAGE_PADDING.ACTIVE_SHAPE;

		// Add the milstone schema to this ticket
		if (data.isGhost) {
			ticketContainer.components.addDynamicComponents(
				ticketSchema.createGhostConstraintSchema(canvasAngularLand, ticketContainer)
			);
		} else {
			ticketContainer.components.addDynamicComponents(
				ticketSchema.createConstraintSchema(canvasAngularLand, ticketContainer)
			);
		}
	}
	/**
	 * Called when the associated DataContainer is updated.
	 * @param {CanvasAngularLand} canvasAngularLand
	 * @param {TicketContainer} ticketContainer
	 */
	public override updateData(canvasAngularLand: CanvasAngularLand, ticketContainer: TicketContainer) {
		super.updateData(canvasAngularLand, ticketContainer);

		const data = ticketContainer.getData();
		const layout = ticketContainer.getLayout();
		if (!data) {
			return;
		}
		// Set the correct texture.
		if (data.view.side === Side.PULL) {
			this.texture = this._pullConstraintTexture;
			this.x = -IMAGE_PADDING.PULL_SHAPE;
			this.y = -IMAGE_PADDING.PULL_SHAPE;
			const width = setTicketWidthHelper(data, layout, this.canvasAngularLand.wideTicketSize$.getValue());
			this.width = width + IMAGE_PADDING.PULL_SHAPE * 2;
			this.height = layout.height + IMAGE_PADDING.PULL_SHAPE * 2;
			this.isPull = true;
			ticketContainer.SURFACE.hitArea = new Circle(layout.width / 2, layout.height / 2, 150);
		} else {
			this.texture = this._activeConstraintTexture;
			this.x = -IMAGE_PADDING.ACTIVE_SHAPE;
			this.y = -IMAGE_PADDING.ACTIVE_SHAPE;
			this.width = layout.width + IMAGE_PADDING.ACTIVE_SHAPE * 2;
			this.height = layout.height + IMAGE_PADDING.ACTIVE_SHAPE * 2;
			this.isPull = false;
			ticketContainer.SURFACE.hitArea = new Circle(layout.width / 2, layout.height / 2, 75);
		}

		//this.alpha = 0;
	}
}
